import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { ThreeDots } from 'react-loader-spinner';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';

import WithLabel, { LABEL_STYLES } from '../../../../components/with-label/WithLabel';
import { Input } from '../../../../components/common-inputs';
import WithError from '../../../../components/with-error/WithError';
import CommonButton from '../../../../components/common-button/CommonButton';
import DatasetTable from './components/DatasetTable';
import DatasetButtons from './components/DatasetButtons';
import StartFromField from './components/StartFromField';
import SonarDatasetModal from '../sonar-dataset-modal/SonarDatasetModal';
import {
  addDataset,
  cancelCreation,
  editCreateSonarProject,
  editCreateSonarProjectUsers,
  editDataset,
} from '../../../../redux/create-sonar/createSonarActions';
import DatasetErrors from './components/DatasetErrors';
import AccessContainers from '../../../../components/access-containers/AccessContainers';
import { getAccessUsers, loadMoreAccessUsers } from '../../../../redux/projects/projectsActions';
import SearchInput from '../../../../components/common-inputs/SearchInput';
import { resetDatasetOptions } from '../../../../redux/sonar-management/sonarManagementReducer';
import CommonDropdown from '../../../../components/common-dropdown/CommonDropdown';
import useProjectRangeTypeOptions from '../../../../hooks/useProjectRangeTypeOptions';

import scss from './SonarCreationForm.module.scss';

const SonarCreationForm = ({ handleSubmit }) => {
  const { t } = useTranslation('projects');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { restart, change } = useForm();
  const { submitting, values, valid } = useFormState();

  const [isDatasetModalOpen, setIsDatasetModalOpen] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState({});
  const projectRangeTypeOptions = useProjectRangeTypeOptions();

  const isAccessUsersLoading = useSelector((state) => state.projects.isAccessUsersLoading);
  const { accessUsers, totalAccessUsers } = useSelector((state) => state.projects);
  const isDatasetLoading = useSelector((state) => state.createSonar.isDatasetLoading);
  const { id } = useSelector((state) => state.createSonar.projectInfo);

  const isLoading = submitting;

  const inactiveUsers = useMemo(
    () => accessUsers.filter((user) => !values.activeUsers.some((activeUser) => activeUser.id === user.id)),
    [values, accessUsers],
  );

  const handleSave = async () => {
    await handleSubmit();

    if (valid) {
      navigate('/projects');
    }
  };

  const handleSaveAndCollect = async () => {
    await handleSubmit();

    if (id && valid) {
      navigate(`/project/sonar/collect-data/${id}`);
    }
  };

  const handleAddDataset = async (values) => {
    await dispatch(addDataset(values));
  };

  const handleEditDataset = async (values) => {
    await dispatch(editDataset({ values, datasetId: selectedDataset.id }));
    setSelectedDataset({});
  };

  const handleOpenAddDatasetModal = useCallback(() => {
    setIsDatasetModalOpen(true);
  }, []);

  const handleOpenEditDatasetModal = useCallback((dataset) => {
    setIsDatasetModalOpen(true);
    setSelectedDataset(dataset);
  }, []);

  const handleCloseAddDatasetModal = useCallback(() => {
    setIsDatasetModalOpen(false);
    setSelectedDataset({});
  }, []);

  const handleCancel = useCallback(() => {
    restart();
    dispatch(cancelCreation());
    navigate('/projects/create');
  }, [restart, navigate, dispatch]);

  const handleEditProject = useCallback(
    (key, value) => {
      dispatch(editCreateSonarProject({ ...values, [key]: value }));
    },
    [values, dispatch],
  );

  const handleActiveUser = useCallback(
    (user) => {
      const newUsers = [...values.activeUsers, user];

      change('activeUsers', newUsers);
      dispatch(editCreateSonarProjectUsers(newUsers));
    },
    [values, change, dispatch],
  );

  const handleInactiveUser = useCallback(
    ({ id }) => {
      const newUsers = values.activeUsers.filter((user) => user.id !== id);

      change('activeUsers', newUsers);
      dispatch(editCreateSonarProjectUsers(newUsers));
    },
    [values, change, dispatch],
  );

  const handleSearchUsers = useCallback(
    (newSearch) => {
      change('assignUser', newSearch);
      dispatch(getAccessUsers({ search: newSearch, page: 1 }));
    },
    [change, dispatch],
  );

  const handleLoadMoreUsers = useCallback(
    (newPage) => {
      dispatch(loadMoreAccessUsers({ search: values.assignUser ?? '', page: newPage }));
    },
    [dispatch, values.assignUser],
  );

  useEffect(() => {
    dispatch(getAccessUsers({ search: '', page: 1 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      dispatch(resetDatasetOptions());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <form className={scss.form} onSubmit={handleSubmit}>
      <Field name='projectName' autoComplete='project-name'>
        {({ meta, input }) => (
          <WithLabel
            variant={LABEL_STYLES.ALIGNED}
            label={`${t('projectName')}:`}
            isBold
            extraClassName={scss.inputRow}>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <Input
                type='text'
                size='sm'
                isError={meta.touched && meta.error}
                disabled={isLoading}
                autoComplete='project-name'
                value={input.value}
                name={input.name}
                onFocus={input.onFocus}
                onChange={input.onChange}
                onBlur={(e) => {
                  input.onBlur(e);
                  handleEditProject(e.target.name, e.target.value);
                }}
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      <Field name='brand' autoComplete='sonar-brand'>
        {({ meta, input }) => (
          <WithLabel variant={LABEL_STYLES.ALIGNED} label={`${t('brand')}:`} isBold extraClassName={scss.inputRow}>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <Input
                type='text'
                size='sm'
                isError={meta.touched && meta.error}
                disabled={isLoading}
                autoComplete='sonar-brand'
                value={input.value}
                name={input.name}
                onFocus={input.onFocus}
                onChange={input.onChange}
                onBlur={(e) => {
                  input.onBlur(e);
                  handleEditProject(e.target.name, e.target.value);
                }}
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      <Field name='rangeType'>
        {({ meta, input }) => (
          <WithLabel variant={LABEL_STYLES.ALIGNED} label={`${t('rangeType')}:`} isBold extraClassName={scss.inputRow}>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <CommonDropdown
                value={input.value}
                options={projectRangeTypeOptions}
                onSelect={(val) => {
                  input.onChange(val);
                  handleEditProject('rangeType', val);
                }}
                name={input.name}
                onFocus={input.onFocus}
                isError={meta.touched && meta.error}
                disabled={isLoading}
                onBlur={input.onBlur}
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      <StartFromField isLoading={isLoading} onBlur={handleEditProject} />

      <DatasetButtons submitting={submitting || isDatasetLoading} onAddDataset={handleOpenAddDatasetModal} />

      <DatasetErrors />

      <DatasetTable onEdit={handleOpenEditDatasetModal} />

      <Field name='assignUser'>
        {({ input }) => (
          <WithLabel label={t('assignUser')} variant={LABEL_STYLES.ALIGNED} isBold extraClassName={scss.inputRow}>
            <SearchInput
              value={input.value}
              onChange={handleSearchUsers}
              name={input.name}
              size='sm'
              disabled={isLoading || !values.projectName}
            />
          </WithLabel>
        )}
      </Field>

      <Field name='activeUsers'>
        {({ input, meta }) => (
          <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)}>
            <AccessContainers
              onInactive={handleInactiveUser}
              onActive={handleActiveUser}
              inactive={inactiveUsers}
              active={input.value}
              disabled={isLoading || !values.projectName}
              isLoading={isAccessUsersLoading}
              searchStr={values.assignUser}
              onLoadMore={handleLoadMoreUsers}
              totalElements={totalAccessUsers}
              inactiveLoadedSize={accessUsers.length}
            />
          </WithError>
        )}
      </Field>

      <div className={scss.actions}>
        <div className={scss.actionsLeft}>
          <CommonButton type='submit' styleColor='primary' onClick={handleSave} maxWidth={100} disabled={isLoading}>
            {isLoading ? <ThreeDots height={15} width={40} /> : null}
            {t('save')}
          </CommonButton>
          <CommonButton
            type='button'
            styleColor='secondary'
            onClick={handleSaveAndCollect}
            maxWidth={220}
            disabled={isLoading}>
            {isLoading ? <ThreeDots height={15} width={40} /> : null}
            {t('saveStartDataCollection')}
          </CommonButton>
        </div>

        <CommonButton type='button' styleColor='secondary' onClick={handleCancel} maxWidth={100} disabled={isLoading}>
          {t('cancel')}
        </CommonButton>
      </div>

      <SonarDatasetModal
        isOpen={isDatasetModalOpen}
        onClose={handleCloseAddDatasetModal}
        onAdd={handleAddDataset}
        datasetId={selectedDataset.id}
        onEdit={handleEditDataset}
      />
    </form>
  );
};

export default memo(SonarCreationForm);
